import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useEffect, useState } from 'react';
import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Scale, ScrollText } from 'lucide-react';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg text-muted-foreground leading-relaxed transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

const MaximCategoryCard = ({ 
  title, 
  description, 
  icon: Icon, 
  href, 
  count, 
  delay = 0 
}: { 
  title: string; 
  description: string; 
  icon: any; 
  href: string; 
  count: number;
  delay?: number; 
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <Link 
      to={href}
      className={`group p-8 bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl border border-gold/20 
        hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20
        ${isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}
    >
      <div className="flex flex-col md:flex-row md:items-start space-y-4 md:space-y-0 md:space-x-6">
        <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-primary to-gold text-white rounded-xl flex items-center justify-center mx-auto md:mx-0">
          <Icon className="h-8 w-8" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="font-rockwell text-2xl font-semibold text-primary group-hover:text-gold transition-colors mb-4">
            {title}
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            {description}
          </p>
          <div className="flex items-center justify-center md:justify-start text-sm font-medium text-primary group-hover:text-gold transition-colors">
            <span>Explore</span>
            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const MaximsOverviewPage = () => {
  const maximCategories = [
    {
      title: "Maxims of Equity",
      description: "Explore the fundamental principles that govern equitable jurisdiction, ensuring fairness, justice, and good conscience prevail where strict legal rules may produce unjust results.",
      icon: Scale,
      href: "/maxims-of-equity",
      count: 30
    },
    {
      title: "Maxims of Commercial Law", 
      description: "Discover the time-tested legal principles that form the foundation of commercial jurisprudence, providing guidance for the interpretation and application of commercial legal rules.",
      icon: BookOpen,
      href: "/maxims-of-commercial-law",
      count: 10
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <SEO
        title="Maxims of Law | Freeman & Crown Law"
        description="Explore comprehensive collections of legal maxims including Maxims of Equity and Maxims of Commercial Law. Fundamental principles that guide justice, fairness, and legal interpretation."
        image="/lovable-uploads/d54b2ac6-69a1-4d86-ac6e-e08d24d13ec9.png"
        url={window.location.origin + '/maxims-of-law'}
        type="website"
      />
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header Section */}
          <div className="text-center mb-20">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-8">
              Maxims of Law
            </h1>
            
            {/* Featured Quote */}
            <div className="mb-8 text-center">
              <blockquote>
                <p className="text-lg font-medium text-foreground italic mb-3">
                  "Maxims are the condensed good sense of nations."
                </p>
                <cite className="text-muted-foreground">— Sir James Mackintosh</cite>
              </blockquote>
            </div>
            
            <div className="max-w-3xl mx-auto lg:max-w-none lg:mx-8 space-y-6">
              <AnimatedParagraph delay={200}>
                Legal maxims are enduring principles that form the backbone of justice, distilling centuries of wisdom into clear rules of fairness. At Freeman & Crown Equity Law Group, these maxims guide us through complexity, ensuring justice is pursued with both precision and conscience.
              </AnimatedParagraph>
            </div>
          </div>

          {/* Maxim Categories */}
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="font-rockwell text-3xl font-semibold text-primary mb-6">
                Explore Powerful Maxims
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Dive deep into the principles that govern both legal and equitable jurisprudence, 
                each collection offering unique insights into the pursuit of justice.
              </p>
            </div>
            
            <div className="grid gap-8 max-w-4xl mx-auto">
              {maximCategories.map((category, index) => (
                <MaximCategoryCard
                  key={index}
                  title={category.title}
                  description={category.description}
                  icon={category.icon}
                  href={category.href}
                  count={category.count}
                  delay={800 + (index * 200)}
                />
              ))}
            </div>
          </div>

          {/* What are Maxims Section */}
          <h3 className="font-rockwell text-2xl font-semibold text-primary mb-6 text-center mt-20">
            The Power of Legal Maxims
          </h3>
          <div className="p-8 bg-gradient-to-r from-primary/5 to-gold/5 rounded-xl border border-gold/20">
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-primary mb-3">Historical Foundation</h4>
                <p className="text-muted-foreground">
                  Legal maxims have evolved through centuries of judicial wisdom, crystallizing the most essential 
                  principles of law into concise, memorable statements that guide legal reasoning.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-primary mb-3">Practical Application</h4>
                <p className="text-muted-foreground">
                  These principles serve as both interpretive tools and decision-making frameworks, 
                  helping legal practitioners navigate complex situations with clarity and consistency.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-primary mb-3">Universal Wisdom</h4>
                <p className="text-muted-foreground">
                  Maxims transcend specific jurisdictions, embodying universal principles of justice 
                  that resonate across different legal systems and cultures.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-primary mb-3">Living Principles</h4>
                <p className="text-muted-foreground">
                  While rooted in tradition, maxims remain relevant in modern legal practice, 
                  adapting to contemporary challenges while maintaining their core wisdom.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default MaximsOverviewPage;